import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useInView } from 'react-intersection-observer';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReactTable from '../../Table';
import TableSkeleton from '../../Table/TableSkeleton';
import Typography from '../../Typography';
import { useUsageLedger } from '../../../hooks/billing';
import { buildLedgerRowRoute, isLedgerRowClickable } from './isLedgerRowClickable';
import { formatLedgerCredits } from '../../../utils/constants/creditLedgerEntryTypes';

/**
 * ROAR-4943 — the usage ledger (AC3), matching Figma node 263-490.
 *
 * **Deep links go through `onNavigate`, never `navigate`** (ROAR-4899 §4 decision 2). `<Header/>`
 * mounts above `<Routes>` (`containers/App.js:149-151`) and never unmounts, so this component and
 * the Settings dialog survive a route change; the shell's `onNavigate` closes the modal first.
 *
 * **Only the ACTIVITY cell is the click target, not the whole row.** `components/Table/index.js`
 * exposes no row-click prop and adding one would change a table every screen in the app renders —
 * out of this slice. The cell is a real `<button>`, so it is keyboard reachable.
 *
 * Paging is a `useInView` sentinel below the table rather than a control, mirroring
 * `containers/pages/Admin/Dashboard/CustomerList.js:30-33,168` — `ReactTable` registers no
 * pagination plugin.
 *
 * Filters are **staged**: the panel edits draft values and only APPLY FILTER promotes them into the
 * query key. Applying on every keystroke would refetch the ledger from page 1 on each change.
 */

const EMPTY_FILTERS = {
  period: 'all',
  userId: undefined,
};

/**
 * The TIME PERIOD facet is a preset list, not a pair of date pickers: the design gives the facet a
 * single control, and every preset it offers is a trailing window ending today, so the two dates
 * are derivable and a free range would be a control the panel has nowhere to put.
 *
 * `days` absent means "no bound at all" — that is what distinguishes All Time from a window.
 */
const TIME_PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
];

/**
 * A preset to the `from`/`to` the ledger endpoint actually takes.
 *
 * `days - 1`, because the windows are inclusive of today: "Last 7 days" is today plus the six
 * before it, not today plus seven.
 *
 * @param {string} value A `TIME_PERIODS` value.
 * @returns {{from: string|undefined, to: string|undefined}} Day-granular bounds, or both undefined.
 */
const periodToRange = (value) => {
  const preset = TIME_PERIODS.find((option) => option.value === value);
  if (!preset?.days) return { from: undefined, to: undefined };
  return {
    from: moment()
      .subtract(preset.days - 1, 'days')
      .format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
  };
};

/** Keystroke settling time before the search term enters the query key. */
const SEARCH_DEBOUNCE_MS = 400;

/** `"<display_name> on <asset_label>"`, or just the activity when the row has no asset. */
const activityLabel = (row) => [row?.display_name, row?.asset_label].filter(Boolean).join(' on ');

/**
 * One facet of the filter panel: a closed summary row that expands into its option list in place.
 *
 * The options carry checkboxes but the facet is **single-select** — the endpoint takes one
 * `user_id` and one window, and offering a multi-select that the query cannot
 * express would be a control that silently drops all but one choice. Picking the "All …" option is
 * how a facet is cleared, so every state the list can be in is reachable from the list itself.
 *
 * The rows are real `<button>`s rather than `<input type="checkbox">`: the square is a selection
 * indicator for a listbox, not an independently togglable value.
 */
const LedgerFacet = ({ label, testId, value, options, open, onToggle, onSelect }) => {
  const selected = options.find((option) => (option.value || '') === (value || ''));

  return (
    <div data-testid={testId}>
      <Typography className={'text-body text-[12px] font-semibold uppercase tracking-wide'}>
        {label}
      </Typography>
      <button
        type={'button'}
        aria-haspopup={'listbox'}
        aria-expanded={open}
        className={
          'mt-1 flex w-full items-center justify-between rounded border border-[#DADADA] px-3 py-2 text-left text-xs text-headings font-weight-400'
        }
        onClick={onToggle}
      >
        <span>{selected?.label || options[0]?.label}</span>
        {open ? (
          <KeyboardArrowUpIcon fontSize={'small'} className={'text-body'} />
        ) : (
          <KeyboardArrowDownIcon fontSize={'small'} className={'text-body'} />
        )}
      </button>
      {open && (
        <div role={'listbox'} className={'mt-2 rounded bg-primary-50 p-2'}>
          {options.map((option) => {
            const isSelected = (option.value || '') === (value || '');
            return (
              <button
                key={option.value || 'all'}
                type={'button'}
                role={'option'}
                aria-selected={isSelected}
                className={
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-headings font-weight-400'
                }
                onClick={() => onSelect(option.value || undefined)}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                    isSelected
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-grey3 bg-white'
                  }`}
                >
                  {isSelected && <CheckIcon style={{ fontSize: 12 }} />}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

LedgerFacet.propTypes = {
  label: PropTypes.string.isRequired,
  testId: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const UsageLedger = ({ onNavigate }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  // Which facet's option list is expanded, or null. One at a time: the panel is a popover with a
  // fixed width, and two open lists would push APPLY FILTER off the bottom of it.
  const [openFacet, setOpenFacet] = useState(null);
  const panelRef = useRef(null);
  // The member id that was rejected, or null. Holding the ID rather than a boolean is what lets
  // the render below tell the STALE 400 (the one that triggered the clear, still in the cache
  // until the refetch lands) apart from a genuinely new failure — see `isStaleFacetError`.
  const [rejectedMemberId, setRejectedMemberId] = useState(null);

  // The search term is part of the query key, so feeding every keystroke straight in would fire one
  // ledger request per character, each restarting at page 1. Debounced for the same reason the
  // facets are staged behind APPLY FILTER.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  /**
   * The applied window. Recomputed only when the preset changes: `periodToRange` reads the clock,
   * and calling it on every render would hand `useUsageLedger` a fresh object each time.
   */
  const appliedRange = useMemo(() => periodToRange(filters.period), [filters.period]);

  const {
    data,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    dataUpdatedAt,
  } = useUsageLedger({
    search: debouncedSearch,
    ...appliedRange,
    userId: filters.userId,
  });
  const { ref, inView } = useInView();

  /**
   * The filter panel's option lists, served on the ledger response itself rather than by a second
   * request. Every page carries the same lists — they are company-wide and NOT narrowed by the
   * facets in the query key — so the first page is as good as any.
   *
   * HELD in state rather than read straight off `data` because an errored read has no pages to
   * read them from, and this pane's recovery path runs through exactly that state: a stale Member
   * facet 400s, and the effect below clears it. Deriving the options inline would empty the Member
   * dropdown on that render, so a panel left open would show the user an empty list at the very
   * moment they need to pick a different member. Keeping the last lists we were served holds the
   * panel steady until the cleared-facet refetch lands.
   */
  const servedFilters = data?.pages?.[0]?.filters;
  const [filterOptions, setFilterOptions] = useState(null);
  useEffect(() => {
    if (servedFilters) setFilterOptions(servedFilters);
  }, [servedFilters]);

  // Flatten every page — the table renders the whole accumulated list, not just the latest page.
  const rows = useMemo(() => (data?.pages || []).flatMap((page) => page?.items || []), [data]);

  // `isFetchingNextPage` is in the dependency list on purpose: when the sentinel stays in view
  // after a page lands — a short pane with a tall viewport — `inView` and `hasNextPage` both stay
  // true, the deps never change, and paging would dead-end. The fetch flag flipping true then
  // false is what re-runs this and pulls the following page.
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * ROAR-4945 — a rejected Member facet is an invalid filter, not a broken pane.
   *
   * The backend rejects `user_id` with a **400** both when it is malformed and when it is a valid
   * id outside the scoped company, answering identically in both cases on purpose: a distinct
   * "exists but not yours" would turn the endpoint into a cross-tenant user-existence oracle. The
   * FE must therefore not try to tell the two apart either.
   *
   * This is reachable in normal use. The Member dropdown is built from `/filters` `members[]`,
   * which fills GRADUALLY — nothing backfills rows predating ROAR-4953's actor stamp — so a member
   * who was selectable can drop out of a later refetch while their selection is still applied.
   * Left alone the pane showed its generic "couldn't load" error with the dead filter still set,
   * which is a dead end: every retry re-sends the same rejected id.
   *
   * Clearing the facet changes the query key, so the unfiltered read is issued automatically.
   *
   * KNOWN LIMITATION, accepted: the branch keys off the STATUS alone, so an unrelated 400 raised
   * while a member facet happens to be applied is mis-attributed to that facet — the filter is
   * cleared and the notice below is shown for something that was not its fault. The alternative is
   * matching the server's message text, which couples this pane to backend copy and breaks
   * silently when that copy changes. `user_id` is the only documented 400 on this endpoint, and
   * the failure mode is a wrong explanation rather than lost data or a stuck pane, so the status
   * check is the better trade. Revisit if the endpoint grows other 400s.
   *
   * Deliberately does NOT read `error.response.data`: the backend answers identically for a
   * malformed id and for a valid id belonging to another company, precisely so it cannot be used
   * as a cross-tenant user-existence oracle. Rendering its message, or branching on it, would be
   * the client re-opening that oracle.
   */
  useEffect(() => {
    if (!isError) return;
    if (error?.response?.status !== 400) return;
    // Nothing to clear means this 400 came from something else — do not invent a filter problem.
    if (!filters.userId) return;

    setRejectedMemberId(filters.userId);
    setFilters((current) => ({ ...current, userId: undefined }));
    // The panel's draft is seeded from `filters` only when it OPENS, so a stale draft would put
    // the dead member straight back on the next APPLY.
    setDraft((current) => ({ ...current, userId: undefined }));
  }, [isError, error, filters.userId]);

  const columns = useMemo(
    () => [
      {
        Header: 'DATE',
        accessor: 'occurred_at',
        // Client-side sorting is disabled on every column: the table holds only the pages loaded
        // so far, so a header sort would silently reorder a partial list and look authoritative.
        disableSortBy: true,
        Cell: ({ value }) => (value ? moment(value).format('MM-DD-YYYY, hh:mm A') : '-'),
      },
      {
        Header: 'USER',
        accessor: 'user',
        disableSortBy: true,
        Cell: ({ value }) => value?.name || '-',
      },
      {
        Header: 'ACTIVITY',
        id: 'activity',
        accessor: (row) => row,
        disableSortBy: true,
        Cell: ({ value: row }) => {
          const label = activityLabel(row);
          if (!isLedgerRowClickable(row)) {
            // Never link somewhere guessed: an unsettled row's target does not exist yet, and an
            // unmapped pair has no route at all.
            return <span>{label}</span>;
          }
          return (
            <button
              type={'button'}
              className={'link text-left text-secondary-700 underline'}
              onClick={() => onNavigate?.(buildLedgerRowRoute(row))}
            >
              {label}
            </button>
          );
        },
      },
      {
        Header: 'CREDITS USED',
        id: 'credits_used',
        // ROAR-4945: the whole row, because the cell needs `entry_type` as well as the magnitude.
        // `credits_used` is Math.abs(amount) on the wire and the DIRECTION lives in `entry_type`,
        // so rendering the accessor alone made a refund of 50 identical to a spend of 50.
        accessor: (row) => row,
        disableSortBy: true,
        // `column.className` reaches the <th> only (`components/Table/index.js:79`); the body <td>
        // takes no className, so the alignment has to come from the cell's own content.
        className: 'text-right',
        Cell: ({ value: row }) => <div className={'text-right'}>{formatLedgerCredits(row)}</div>,
      },
    ],
    [onNavigate]
  );

  const applyFilters = () => {
    setRejectedMemberId(null);
    setFilters(draft);
    setPanelOpen(false);
    setOpenFacet(null);
  };

  const clearFilters = () => {
    setRejectedMemberId(null);
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPanelOpen(false);
    setOpenFacet(null);
  };

  /**
   * Opening the panel re-seeds the draft from what is actually applied. Without this, dismissing
   * the panel without pressing APPLY FILTER leaves the abandoned selections showing next time,
   * making the panel disagree with the rows underneath it.
   */
  const togglePanel = () => {
    setPanelOpen((open) => {
      if (!open) setDraft(filters);
      return !open;
    });
    setOpenFacet(null);
  };

  /**
   * The panel is a popover over the table, so a click anywhere outside it dismisses it — without
   * this it would sit on top of the rows it filters with no way to close but the FILTER button.
   * Dismissing this way is the same as closing without applying: the draft is re-seeded on reopen.
   */
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onPointerDown = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setPanelOpen(false);
        setOpenFacet(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [panelOpen]);

  const memberOptions = useMemo(
    () => [
      { value: '', label: 'All Members' },
      ...(filterOptions?.members || []).map((member) => ({
        value: String(member.id),
        label: member.name,
      })),
    ],
    [filterOptions]
  );

  // APPLY FILTER stays inert until the draft actually differs from what is applied, so pressing it
  // can never re-issue the read the table is already showing.
  const isDraftDirty =
    draft.period !== filters.period || (draft.userId || '') !== (filters.userId || '');

  const selectFacet = (key) => (value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setOpenFacet(null);
  };

  const toggleFacet = (key) => () => setOpenFacet((current) => (current === key ? null : key));

  // The 400 that caused the clear is still the cached error until the unfiltered refetch resolves.
  // Suppressing on the FLAG alone made the suppression permanent, hiding every later failure
  // behind a notice about a filter that was already dealt with.
  const isStaleFacetError = isError && error?.response?.status === 400 && rejectedMemberId != null;

  return (
    <div className={'mt-6'}>
      <Typography className={'text-headings text-sm font-semibold'}>Usage ledger</Typography>
      <Typography className={'text-body text-xs'}>
        Credits are only deducted once a test completes.
      </Typography>

      <div className={'mt-3 flex items-center gap-3'}>
        <div className={'flex grow items-center gap-2 rounded border border-grey3 px-3 py-2'}>
          <SearchIcon fontSize={'small'} className={'text-body'} />
          <input
            className={'w-full outline-none'}
            placeholder={'Search member or activity'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {/* The panel is anchored to the FILTER button and overlays the table rather than pushing
            it down: it is a popover in the design, and a panel that reflowed the rows underneath
            would move the very list the user is filtering out from under the pointer. */}
        <div className={'relative shrink-0'} ref={panelRef}>
          <button
            type={'button'}
            className={'flex items-center gap-2 rounded bg-primary-50 px-4 py-2 text-primary-700'}
            onClick={togglePanel}
          >
            <TuneIcon fontSize={'small'} />
            FILTER
          </button>

          {panelOpen && (
            <div
              className={
                'absolute right-0 top-full z-20 mt-2 w-[300px] rounded bg-white p-4 shadow-lg ring-1 ring-grey3'
              }
            >
              <div className={'flex flex-col gap-4'}>
                <LedgerFacet
                  label={'Time period'}
                  testId={'ledger-filter-period'}
                  value={draft.period}
                  options={TIME_PERIODS}
                  open={openFacet === 'period'}
                  onToggle={toggleFacet('period')}
                  onSelect={(value) => selectFacet('period')(value || 'all')}
                />

                <LedgerFacet
                  label={'Member'}
                  testId={'ledger-filter-member'}
                  value={draft.userId}
                  options={memberOptions}
                  open={openFacet === 'member'}
                  onToggle={toggleFacet('member')}
                  onSelect={selectFacet('userId')}
                />
              </div>

              <div
                className={
                  'mt-4 flex items-center justify-between gap-3 border-t border-[#DADADA] pt-4'
                }
              >
                <button
                  type={'button'}
                  className={
                    'rounded border border-primary-500 px-4 py-2 text-sm font-medium text-primary-500'
                  }
                  onClick={clearFilters}
                >
                  CLEAR ALL
                </button>
                <button
                  type={'button'}
                  disabled={!isDraftDirty}
                  className={`rounded px-4 py-2 text-sm font-medium text-white ${
                    isDraftDirty ? 'bg-primary-500' : 'cursor-not-allowed bg-[#0000004D]'
                  }`}
                  onClick={applyFilters}
                >
                  APPLY FILTER
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && <TableSkeleton rowsNum={5} />}

      {/* The member the filter named is gone, not the ledger — say which, and note that the list
          below is now unfiltered rather than leaving the change unexplained. */}
      {rejectedMemberId != null && (
        <Typography variant={'body2'} className={'mt-4 text-body'}>
          That member is no longer available to filter by, so the filter was cleared.
        </Typography>
      )}

      {/* A failed read must not masquerade as "No usage yet" — the difference between "you have
          not used credits" and "we could not load your usage" matters on a billing screen.
          The ONE error suppressed here is the stale 400 that the notice above already explains:
          it is still in the cache until the cleared-filter refetch lands, and reporting it twice
          would contradict the notice. Any OTHER failure — a 500, an outage — must still surface,
          or a rejected facet would permanently swallow every later error and leave a blank pane. */}
      {!isLoading && isError && !isStaleFacetError && (
        <Typography variant={'body2'} className={'mt-4 text-body'}>
          Couldn&apos;t load your usage ledger. Try again.
        </Typography>
      )}

      {!isLoading && !isError && (
        <>
          {/* The frame class only when there are no rows: with none, the table's own outline hugs
              the header and leaves the empty state outside it (see `.settings-table-frame`). */}
          <div className={`mt-4 ${rows.length === 0 ? 'settings-table-frame' : ''}`}>
            <ReactTable
              data={rows}
              columns={columns}
              dependencyArray={[rows, onNavigate]}
              className={'table settings-table w-full'}
              headerClassName={'background rounded [&_th]:text-[10px] [&_th]:!font-bold'}
              bodyClassName={'text-headings'}
              emptyDataText={'No usage yet'}
            />
          </div>
          {hasNextPage && (
            <div data-testid={'usage-ledger-sentinel'} className={'h-px'} ref={ref} />
          )}
        </>
      )}

      <div className={'mt-3 flex items-center gap-2'}>
        <Typography variant={'caption'} className={'text-body'}>
          {/* Derived client-side from react-query — "last updated" is not a contract field. */}
          {`Last updated: ${dataUpdatedAt ? moment(dataUpdatedAt).fromNow() : '-'}`}
        </Typography>
        <button type={'button'} aria-label={'Refresh usage ledger'} onClick={() => refetch()}>
          <RefreshIcon fontSize={'small'} className={'text-body'} />
        </button>
      </div>
    </div>
  );
};

UsageLedger.propTypes = {
  /** The Settings shell's close-then-navigate wrapper. Panes must never call navigate directly. */
  onNavigate: PropTypes.func,
};

export default UsageLedger;
